import { logActions } from "./actions/copilot-action-log-actions";
import type { CopilotActionType } from "./types/db-types";
import type { PageComponent } from "../_types";

/**
 * Component ids updated via `updateComponent(id, updates, "ai")` since the last `commitBatch` —
 * consulted (and cleared) there to skip the manual-edit diff log for ids the AI already logged
 * itself, so the same edit isn't double-logged under two different sources. Module-level rather
 * than store state: it's pure bookkeeping for `commitBatch`, not app state any UI reads.
 */
export const aiTouchedComponentIds = new Set<string>();

/**
 * Fire-and-forget audit-log write for a manual (non-AI) edit — unifies the chat history panel
 * and the copilot's grounding context across both manual and AI-driven changes (ENG-147
 * follow-up). `session_id` is null (a manual edit isn't part of any AI chat session); `tier` is
 * always "content" since none of the manual mutation paths this logs touch access/password.
 * Silently no-ops before `pageId` is known (store not yet initialized) — there's nothing to
 * attach the log row to yet, and nothing else in this early window can generate a real edit.
 * @param getPageId - Reads the current store's `pageId` at call time (not a snapshot), since the
 *   store may initialize between when a mutation queues this call and when it actually runs.
 * @param input - The action's audit-log fields.
 */
export function logManualAction(
  getPageId: () => number | null,
  input: {
    actionType: CopilotActionType;
    componentId: string | null;
    description: string;
    beforeState: PageComponent | null;
    afterState: PageComponent | null;
    isRevertible: boolean;
  }
): void {
  const pageId = getPageId();
  if (!pageId) return;

  logActions([{ pageId, sessionId: null, tier: "content", source: "manual", ...input }]).then((result) => {
    if (!result.success) {
      console.error(
        "logManualAction: Failed to log manual edit",
        { pageId, actionType: input.actionType },
        result.error
      );
    }
  });
}
